import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import config from './config'; // adjust path if needed

/**
 * Extracts a hotel array from whatever shape the API returns.
 * Handles: plain array, { data: [] }, { hotels: [] }, { result: [] }, etc.
 */
function extractHotels(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  // Common wrapper shapes
  for (const key of ['data', 'hotels', 'result', 'results', 'items', 'value']) {
    if (Array.isArray(raw[key])) return raw[key];
  }
  // Single object with an id/hotelId — API returned one hotel directly
  if (raw.id || raw.Id || raw.hotelId || raw.HotelId) return [raw];
  return [];
}

export default function SetupGuard({ children }) {
  // 'checking' | 'show-wizard' | 'redirect'
  const [status, setStatus] = useState('checking');

  useEffect(() => {
    const check = async () => {
      const companyId = sessionStorage.getItem('companyId');

      // Restaurant wizard saves a per-company draft on every mutation and
      // clears it on success/skip. If a draft is present, setup is mid-flow
      // and we must NOT redirect — otherwise refreshing after Step 1 (which
      // creates a HotelMaster row) would boot the user to the dashboard.
      const wizardDraftKey = `restaurantWizardDraft_v1_${companyId || 'anon'}`;
      if (localStorage.getItem(wizardDraftKey)) {
        setStatus('show-wizard');
        return;
      }

      // Fast path: already flagged locally
      if (localStorage.getItem('hotelSetupDone') === 'true') {
        setStatus('redirect');
        return;
      }

      const token = sessionStorage.getItem('token');

      if (!token || !companyId) {
        setStatus('show-wizard');
        return;
      }

      try {
        const res = await fetch(
          `${config.API_BASE_URL}/Home/GetHotels/${encodeURIComponent(companyId)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        if (res.ok) {
          const raw = await res.json().catch(() => null);
          const hotels = extractHotels(raw);

          console.debug('[SetupGuard] raw response:', raw);
          console.debug('[SetupGuard] extracted hotels:', hotels);

          if (hotels.length > 0) {
            localStorage.setItem('hotelSetupDone', 'true');
            setStatus('redirect');
          } else {
            setStatus('show-wizard');
          }
        } else {
          // Non-200 — fail open, show wizard
          console.warn('[SetupGuard] GetHotels returned', res.status);
          setStatus('show-wizard');
        }
      } catch (e) {
        console.warn('[SetupGuard] Network error:', e);
        setStatus('show-wizard');
      }
    };

    check();
  }, []);

  if (status === 'checking') {
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          background: '#F8FAFF',
          fontFamily: 'Inter, sans-serif',
        }}>
        <CircularProgress sx={{ color: '#1A56DB' }} />
        <Typography sx={{ color: '#64748B', fontSize: 14 }}>
          Checking property status…
        </Typography>
      </Box>
    );
  }

  if (status === 'redirect') {
    const dest =
      sessionStorage.getItem('propertType') === 'R'
        ? '/admin/resturantdashbord'
        : '/admin/dashboard';
    return <Navigate to={dest} replace />;
  }

  return children;
}
